import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  Modal,
} from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Colors, SIZES } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { firestoreService } from '../services/firestoreService';
import { Button } from '../components/Button';
import PlacesAutocomplete from '../components/PlacesAutocomplete';
import { volver } from '../utils/volver';
import { mensajeDeError } from '../utils/mensajeDeError';
import { bloquearSiInvitado } from '../services/modoInvitado';

/**
 * Mis direcciones — el usuario registra Casa, Trabajo, etc.
 *
 * Diseño:
 *   - Subcolección /users/{uid}/addresses
 *   - Una marcada como default; las demás disponibles
 *   - Si la pantalla se abre con ?pick=1 actúa como selector y devuelve la
 *     dirección elegida vía volver(router) + parámetro en la query del cart.
 *
 * Importante: NO usa Google Places (eso requiere la key paga). Ingreso
 * manual de texto. Cuando tengas la key Maps configurada, podemos cambiar
 * el TextInput por un Autocomplete sin tocar el resto del flujo.
 */
export default function AddressesScreen() {
  const router = useRouter();
  const { pick } = useLocalSearchParams<{ pick?: string }>();
  const userFirebaseUid = useAppStore((s) => s.userFirebaseUid);
  const setSelectedDeliveryAddress = useAppStore((s) => s.setSelectedDeliveryAddress);

  const [addresses, setAddresses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [label, setLabel] = useState('');
  const [line1, setLine1] = useState('');
  const [pickedLat, setPickedLat] = useState<number | null>(null);
  const [pickedLng, setPickedLng] = useState<number | null>(null);
  const [isDefault, setIsDefault] = useState(false);
  const [reference, setReference] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!userFirebaseUid) {
      setLoading(false);
      return;
    }
    const unsub = firestoreService.listenToUserAddresses(userFirebaseUid, (items) => {
      setAddresses(items);
      setLoading(false);
    });
    return () => unsub();
  }, [userFirebaseUid]);

  const handleAdd = async () => {
    // Antes esto era `if (!userFirebaseUid) return;` a secas: el invitado
    // llenaba etiqueta y dirección, tocaba "Guardar" y no pasaba
    // absolutamente nada. Ese `return` mudo es el vc13 literal.
    if (bloquearSiInvitado('guardar una dirección')) return;
    if (!userFirebaseUid) {
      Alert.alert('Sesión requerida', 'Inicia sesión para guardar tus direcciones.');
      return;
    }
    if (!label.trim() || !line1.trim()) {
      Alert.alert('Datos incompletos', 'Etiqueta y dirección son obligatorios.');
      return;
    }
    setSaving(true);
    try {
      await firestoreService.addUserAddress(userFirebaseUid, {
        label: label.trim(),
        line1: line1.trim(),
        lat: pickedLat ?? undefined,
        lng: pickedLng ?? undefined,
        isDefault: isDefault || addresses.length === 0, // primera siempre default
        reference: reference.trim() || undefined,
      });
      setLabel('');
      setLine1('');
      setPickedLat(null);
      setPickedLng(null);
      setIsDefault(false);
      setReference('');
      setModalVisible(false);
    } catch (e: any) {
      Alert.alert('No se guardó', mensajeDeError(e, 'No pudimos guardar la dirección. Vuelve a intentarlo.'));
    } finally {
      setSaving(false);
    }
  };

  const handleSelect = (addr: any) => {
    if (pick === '1') {
      setSelectedDeliveryAddress({
        id: addr.id,
        label: addr.label,
        line1: addr.line1,
        lat: addr.lat ?? null,
        lng: addr.lng ?? null,
      });
      volver(router);
    } else {
      // En vista normal, hacer default
      if (userFirebaseUid && !addr.isDefault) {
        firestoreService.setAddressAsDefault(userFirebaseUid, addr.id).catch(() => {});
      }
    }
  };

  const handleDelete = (addr: any) => {
    if (!userFirebaseUid) return;
    Alert.alert('Eliminar dirección', `¿Eliminar "${addr.label}"?`, [
      { text: 'Cancelar', style: 'cancel' },
      {
        text: 'Eliminar',
        style: 'destructive',
        onPress: async () => {
          try {
            await firestoreService.deleteUserAddress(userFirebaseUid, addr.id);
          } catch (e: any) {
            Alert.alert('Error', mensajeDeError(e, 'No pudimos eliminar la dirección. Intenta de nuevo.'));
          }
        },
      },
    ]);
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => volver(router)} style={styles.backBtn}>
          <Ionicons name="arrow-back" size={24} color={Colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>
          {pick === '1' ? 'Elegir dirección' : 'Mis direcciones'}
        </Text>
        <View style={{ width: 24 }} />
      </View>

      <ScrollView contentContainerStyle={{ padding: SIZES.padding }}>
        {loading ? (
          <Text style={styles.muted}>Cargando…</Text>
        ) : addresses.length === 0 ? (
          <View style={styles.empty}>
            <Ionicons name="location-outline" size={64} color={Colors.border} />
            <Text style={styles.emptyTitle}>Aún no tienes direcciones</Text>
            <Text style={styles.muted}>
              Agrega tu casa o trabajo para usarlos al pedir comida.
            </Text>
          </View>
        ) : (
          addresses.map((a) => (
            <TouchableOpacity
              key={a.id}
              style={[styles.card, a.isDefault && styles.cardDefault]}
              onPress={() => handleSelect(a)}
              activeOpacity={0.7}
              disabled={pick !== '1' && a.isDefault}
            >
              <View style={styles.cardLeft}>
                <Ionicons
                  name={a.label.toLowerCase().includes('trabaj') ? 'briefcase' : 'home'}
                  size={28}
                  color={Colors.primary}
                />
              </View>
              <View style={{ flex: 1 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Text style={styles.cardLabel}>{a.label}</Text>
                  {a.isDefault && (
                    <View style={styles.defaultBadge}>
                      <Text style={styles.defaultBadgeText}>Predeterminada</Text>
                    </View>
                  )}
                </View>
                <Text style={styles.cardLine}>{a.line1}</Text>
              </View>
              {pick !== '1' && (
                <TouchableOpacity onPress={() => handleDelete(a)} style={{ padding: 8 }}>
                  <Ionicons name="trash-outline" size={20} color="#FF3B30" />
                </TouchableOpacity>
              )}
            </TouchableOpacity>
          ))
        )}

        <TouchableOpacity style={styles.addBtn} onPress={() => setModalVisible(true)}>
          <Ionicons name="add-circle" size={24} color={Colors.primary} />
          <Text style={styles.addBtnText}>Agregar dirección</Text>
        </TouchableOpacity>
      </ScrollView>

      {/* onRequestClose (H37, auditoría 16-sep-2026): sin él, en Android el
          botón Atrás se lo tragaba el Modal y no pasaba nada. Hace lo mismo que
          "Cancelar", incluido no cerrar mientras se guarda. */}
      <Modal
        visible={modalVisible}
        transparent
        animationType="slide"
        onRequestClose={() => { if (!saving) setModalVisible(false); }}
      >
        <View style={styles.modalBg}>
          <View style={styles.modal}>
            <Text style={styles.modalTitle}>Nueva dirección</Text>
            <Text style={styles.fieldLabel}>Etiqueta (ej. Casa, Trabajo)</Text>
            <TextInput
              style={styles.input}
              placeholder="Casa"
              value={label}
              onChangeText={setLabel}
              maxLength={30}
            />
            <Text style={styles.fieldLabel}>Buscar dirección</Text>
            <PlacesAutocomplete
              placeholder="Ej. Ágora Mall, Av. Lincoln…"
              initialValue={line1}
              onPlaceSelected={(p) => {
                setLine1(p.formattedAddress || p.displayName || '');
                setPickedLat(p.lat);
                setPickedLng(p.lng);
              }}
            />
            {!!line1 && (
              <View style={{ marginTop: 8, flexDirection: 'row', alignItems: 'center', backgroundColor: '#E8F5E9', padding: 10, borderRadius: 8 }}>
                <Ionicons name="checkmark-circle" size={16} color="#2E7D32" />
                <Text style={{ marginLeft: 6, color: '#2E7D32', fontSize: 12, fontWeight: '600', flex: 1 }} numberOfLines={1}>
                  {line1}
                </Text>
                {pickedLat !== null && pickedLng !== null && (
                  <View style={{ backgroundColor: '#2E7D32', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 6, marginLeft: 6 }}>
                    <Text style={{ color: '#FFF', fontSize: 9, fontWeight: '800' }}>GPS</Text>
                  </View>
                )}
              </View>
            )}
            <Text style={styles.fieldLabel}>Referencia adicional (opcional)</Text>
            <TextInput
              style={[styles.input, { height: 50 }]}
              placeholder="Ej. Apto 4B, casa amarilla, frente al colmado"
              maxLength={120}
              value={reference}
              onChangeText={setReference}
            />
            <TouchableOpacity
              style={styles.defaultToggle}
              onPress={() => setIsDefault((v) => !v)}
            >
              <Ionicons
                name={isDefault ? 'checkbox' : 'square-outline'}
                size={22}
                color={Colors.primary}
              />
              {/* flex:1 para que el texto BAJE DE LINEA en vez de que lo corten.
                  Sin el, en una pantalla estrecha se lee "Usar como" y ahi se
                  acaba: el usuario no sabe como que. Visto en un telefono real. */}
              <Text style={{ marginLeft: 8, color: Colors.text, flex: 1 }}>
                Usar como predeterminada
              </Text>
            </TouchableOpacity>
            <View style={{ flexDirection: 'row', gap: 10, marginTop: 16 }}>
              <Button
                title="Cancelar"
                onPress={() => setModalVisible(false)}
                style={{ flex: 1, backgroundColor: '#EEE' }}
                textStyle={{ color: Colors.text }}
                disabled={saving}
              />
              <Button
                title={saving ? 'Guardando…' : 'Guardar'}
                onPress={handleAdd}
                style={{ flex: 1 }}
                loading={saving}
                disabled={saving}
              />
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#FFF' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 15,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  backBtn: { padding: 5 },
  headerTitle: { fontSize: 17, fontWeight: '700', color: Colors.text },
  muted: { color: Colors.textSecondary, textAlign: 'center', marginTop: 20 },
  empty: { alignItems: 'center', paddingVertical: 40 },
  emptyTitle: { fontSize: 16, fontWeight: '700', color: Colors.text, marginTop: 10 },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F8F9FA',
    borderRadius: SIZES.radius,
    padding: 14,
    marginBottom: 10,
  },
  cardDefault: { backgroundColor: '#EAF4FF', borderWidth: 1, borderColor: Colors.primary },
  cardLeft: { marginRight: 12 },
  cardLabel: { fontSize: 15, fontWeight: '700', color: Colors.text },
  cardLine: { fontSize: 13, color: Colors.textSecondary, marginTop: 2 },
  defaultBadge: {
    backgroundColor: Colors.primary,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    marginLeft: 8,
  },
  defaultBadgeText: { color: '#FFF', fontSize: 10, fontWeight: '700' },
  addBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    marginTop: 8,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: Colors.primary,
    borderRadius: SIZES.radius,
  },
  addBtnText: { color: Colors.primary, fontWeight: '700', marginLeft: 8 },

  modalBg: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  modal: {
    backgroundColor: '#FFF',
    padding: 20,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
  },
  modalTitle: { fontSize: 18, fontWeight: '700', color: Colors.text, marginBottom: 12 },
  fieldLabel: { fontSize: 13, color: Colors.textSecondary, marginTop: 8, marginBottom: 4 },
  input: {
    borderWidth: 1,
    borderColor: Colors.border,
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    color: Colors.text,
    backgroundColor: '#FAFAFA',
  },
  defaultToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
  },
});
